import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import Peer from "simple-peer";
import { CurrentUserContext, SocketContext } from "../../App";
import sampleAvatar from "../../assets/sampleAvatar.png";
import FALLBACK_ICE_SERVERS, { fetchIceServers } from "../../utils/iceServers";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3080";

function formatMessageTime(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDaySeparator(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (msgDay.getTime() === today.getTime()) return "Today";
  if (msgDay.getTime() === yesterday.getTime()) return "Yesterday";
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

function groupMessagesByDay(messages) {
  const groups = [];
  let currentDay = null;

  for (const msg of messages) {
    const msgDate = new Date(msg.created_at);
    const dayKey = `${msgDate.getFullYear()}-${msgDate.getMonth()}-${msgDate.getDate()}`;

    if (dayKey !== currentDay) {
      currentDay = dayKey;
      groups.push({ type: "separator", label: formatDaySeparator(msg.created_at), key: dayKey });
    }
    groups.push({ type: "message", data: msg, key: `msg-${msg.message_id}` });
  }

  return groups;
}

export default function ChatWindowComponent({
  conversation,
  currentUserId,
  token,
  socket,
  onBack,
}) {
  const { currentUser } = useContext(CurrentUserContext);
  const [inCall, setInCall] = useState(false); // false | "audio" | "video"
  const [messages, setMessages] = useState([]);
  const [newMessageText, setNewMessageText] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [deleteModalMessage, setDeleteModalMessage] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isFirstLoad = useRef(true);

  const conversationId = conversation?.conversation_id;
  const displayName = conversation?.is_group
    ? conversation.group_name || "Group"
    : conversation?.other_fullname || conversation?.other_username || "User";
  const avatarUrl = conversation?.is_group
    ? conversation.group_avatar
    : conversation?.other_avatar;
  const resolvedAvatar = avatarUrl && avatarUrl !== "sampleAvatar.png" ? avatarUrl : null;

  // Fetch messages
  const fetchMessages = useCallback(
    async (beforeMessageId = null) => {
      if (!conversationId || !token) return;
      setLoading(true);
      try {
        const body = { conversation_id: conversationId, limit: 50 };
        if (beforeMessageId) body.before_message_id = beforeMessageId;

        const res = await fetch(`${API_URL}/getMessages`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (data.message === "success") {
          if (beforeMessageId) {
            setMessages((prev) => [...data.messages, ...prev]);
          } else {
            setMessages(data.messages);
          }
          setHasMore(data.hasMore);
        }
      } catch (err) {
        console.error("Error fetching messages:", err);
      }
      setLoading(false);
    },
    [conversationId, token]
  );

  // Load messages on conversation change
  useEffect(() => {
    if (!conversationId) return;
    isFirstLoad.current = true;
    setMessages([]);
    setTypingUsers({});
    fetchMessages();
  }, [conversationId, fetchMessages]);

  // Scroll to bottom on first load / new messages
  useEffect(() => {
    if (isFirstLoad.current && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
      isFirstLoad.current = false;
    }
  }, [messages]);

  // Socket.IO: join/leave room and listen for events
  useEffect(() => {
    if (!socket || !conversationId) return;

    // Join the conversation room (works even if socket is still connecting —
    // Socket.IO buffers emits until connected)
    const joinRoom = () => {
      socket.emit("joinConversation", conversationId);
    };

    joinRoom();

    // Re-join the room after socket reconnects (server loses room membership
    // on disconnect, so we must re-join when the connection is restored)
    const handleReconnect = () => {
      joinRoom();
    };

    const handleNewMessage = (data) => {
      if (String(data.conversation_id) === String(conversationId)) {
        setMessages((prev) => {
          // Prevent duplicate messages (e.g. if sender already added it locally)
          if (data.message?.message_id && prev.some((m) => m.message_id === data.message.message_id)) {
            return prev;
          }
          return [...prev, data.message];
        });
        // Scroll to bottom for new messages
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 50);
      }
    };

    const handleTyping = (data) => {
      if (String(data.conversation_id) === String(conversationId) && data.user_id !== currentUserId) {
        if (data.isTyping) {
          setTypingUsers((prev) => ({ ...prev, [data.user_id]: data.username }));
        } else {
          setTypingUsers((prev) => {
            const copy = { ...prev };
            delete copy[data.user_id];
            return copy;
          });
        }
      }
    };

    const handleMessageDeleted = (data) => {
      if (String(data.conversation_id) === String(conversationId)) {
        setMessages((prev) =>
          prev.map((m) =>
            m.message_id === data.message_id ? { ...m, is_deleted: true } : m
          )
        );
      }
    };

    socket.on("connect", handleReconnect);
    socket.on("newMessage", handleNewMessage);
    socket.on("typing", handleTyping);
    socket.on("messageDeleted", handleMessageDeleted);

    return () => {
      socket.emit("leaveConversation", conversationId);
      socket.off("connect", handleReconnect);
      socket.off("newMessage", handleNewMessage);
      socket.off("typing", handleTyping);
      socket.off("messageDeleted", handleMessageDeleted);
    };
  }, [socket, conversationId, currentUserId]);

  // Send message
  const handleSendMessage = async () => {
    const trimmed = newMessageText.trim();
    if (!trimmed || sending) return;

    setSending(true);
    setNewMessageText("");

    // Emit stop typing
    if (socket) {
      socket.emit("typing", {
        conversation_id: conversationId,
        user_id: currentUserId,
        isTyping: false,
      });
    }

    try {
      const res = await fetch(`${API_URL}/sendMessage`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          content: trimmed,
        }),
      });
      const data = await res.json();
      if (data.message === "success") {
        setMessages((prev) => [...prev, data.newMessage]);
        // Broadcast via socket
        if (socket) {
          socket.emit("newMessage", {
            conversation_id: conversationId,
            message: data.newMessage,
          });
        }
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 50);
      }
    } catch (err) {
      console.error("Error sending message:", err);
    }
    setSending(false);
  };

  // Handle typing indicator
  const handleTypingInput = (e) => {
    setNewMessageText(e.target.value);
    if (socket) {
      socket.emit("typing", {
        conversation_id: conversationId,
        user_id: currentUserId,
        username: "You",
        isTyping: true,
      });
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        socket.emit("typing", {
          conversation_id: conversationId,
          user_id: currentUserId,
          isTyping: false,
        });
      }, 2000);
    }
  };

  // Delete message
  const handleDeleteMessage = async (messageId) => {
    try {
      const res = await fetch(`${API_URL}/deleteMessage`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message_id: messageId }),
      });
      const data = await res.json();
      if (data.message === "success") {
        setMessages((prev) =>
          prev.map((m) =>
            m.message_id === messageId ? { ...m, is_deleted: true } : m
          )
        );
        if (socket) {
          socket.emit("messageDeleted", {
            conversation_id: conversationId,
            message_id: messageId,
          });
        }
      }
    } catch (err) {
      console.error("Error deleting message:", err);
    }
    setDeleteModalMessage(null);
  };

  // Infinite scroll: load more when scrolling to top
  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    if (messagesContainerRef.current.scrollTop === 0 && hasMore && !loading) {
      const oldestMessageId = messages[0]?.message_id;
      if (oldestMessageId) {
        const prevHeight = messagesContainerRef.current.scrollHeight;
        fetchMessages(oldestMessageId).then(() => {
          // Maintain scroll position after loading older messages
          requestAnimationFrame(() => {
            if (messagesContainerRef.current) {
              const newHeight = messagesContainerRef.current.scrollHeight;
              messagesContainerRef.current.scrollTop = newHeight - prevHeight;
            }
          });
        });
      }
    }
  };

  // No conversation selected state
  if (!conversation) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center h-100"
        style={{ backgroundColor: "#1a1a2e", color: "#828282" }}
      >
        <ion-icon
          name="chatbubbles-outline"
          style={{ fontSize: "6rem", marginBottom: "16px" }}
        ></ion-icon>
        <p style={{ fontSize: "1.6rem" }}>Select a conversation to start chatting</p>
      </div>
    );
  }

  const typingUsersList = Object.values(typingUsers);
  const groupedItems = groupMessagesByDay(messages);

  return (
    <div
      className="d-flex flex-column h-100"
      style={{ backgroundColor: "#1a1a2e" }}
    >
      {/* Header */}
      <div
        className="d-flex align-items-center p-3 gap-3"
        style={{
          backgroundColor: "#1e1e2e",
          borderBottom: "1px solid #2a2a3e",
          minHeight: "64px",
        }}
      >
        {/* Back button (mobile) */}
        <button
          className="btn btn-sm d-md-none"
          onClick={onBack}
          style={{ color: "white", border: "none", padding: "4px" }}
        >
          <ion-icon name="arrow-back" style={{ fontSize: "2rem" }}></ion-icon>
        </button>

        {/* Avatar */}
        <div style={{ width: "44px", height: "44px", minWidth: "44px" }}>
          <img
            src={resolvedAvatar || sampleAvatar}
            alt={displayName}
            className="rounded-circle w-100 h-100"
            style={{ objectFit: "cover" }}
          />
        </div>

        {/* Name */}
        <div className="flex-grow-1">
          <div className="text-white fw-bold" style={{ fontSize: "1.6rem" }}>
            {displayName}
          </div>
          {typingUsersList.length > 0 && (
            <div style={{ color: "var(--main-purple)", fontSize: "1.2rem" }}>
              {typingUsersList.length === 1
                ? `${typingUsersList[0]} is typing...`
                : "Several people are typing..."}
            </div>
          )}
        </div>

        {/* Action icons */}
        <div className="d-flex align-items-center gap-3">
          <ion-icon
            name="call-outline"
            style={{ color: "white", fontSize: "2rem", cursor: "pointer" }}
            onClick={() => setInCall("audio")}
          ></ion-icon>
          <ion-icon
            name="videocam-outline"
            style={{ color: "white", fontSize: "2rem", cursor: "pointer" }}
            onClick={() => setInCall("video")}
          ></ion-icon>
        </div>
      </div>

      {/* Messages Area */}
      <div
        ref={messagesContainerRef}
        className="flex-grow-1 px-3 py-2"
        onScroll={handleScroll}
        style={{ overflowY: "auto", overflowX: "hidden" }}
      >
        {/* Loading spinner for older messages */}
        {loading && messages.length > 0 && (
          <div className="text-center py-2">
            <div className="spinner-border spinner-border-sm text-secondary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {/* Initial loading */}
        {loading && messages.length === 0 && (
          <div className="d-flex align-items-center justify-content-center h-100">
            <div className="spinner-border text-secondary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {groupedItems.map((item) => {
          if (item.type === "separator") {
            return (
              <div
                key={item.key}
                className="d-flex align-items-center justify-content-center my-3"
              >
                <span
                  className="px-3 py-1 rounded-pill"
                  style={{
                    backgroundColor: "#2a2a3e",
                    color: "#828282",
                    fontSize: "1.1rem",
                  }}
                >
                  {item.label}
                </span>
              </div>
            );
          }

          const msg = item.data;
          const isMine = msg.sender_id === currentUserId;

          return (
            <div
              key={item.key}
              className={`d-flex mb-2 ${isMine ? "justify-content-end" : "justify-content-start"}`}
            >
              {/* Other user's avatar */}
              {!isMine && (
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    minWidth: "32px",
                    marginRight: "8px",
                    alignSelf: "flex-end",
                  }}
                >
                  <img
                    src={msg.sender_avatar && msg.sender_avatar !== "sampleAvatar.png" ? msg.sender_avatar : sampleAvatar}
                    alt=""
                    className="rounded-circle w-100 h-100"
                    style={{ objectFit: "cover" }}
                  />
                </div>
              )}

              {/* Message bubble */}
              <div
                style={{ maxWidth: "65%" }}
                onContextMenu={(e) => {
                  if (isMine && !msg.is_deleted) {
                    e.preventDefault();
                    setDeleteModalMessage(msg);
                  }
                }}
              >
                {/* Sender name for groups */}
                {conversation.is_group && !isMine && (
                  <div
                    style={{
                      color: "var(--main-purple)",
                      fontSize: "1.1rem",
                      marginBottom: "2px",
                      marginLeft: "4px",
                    }}
                  >
                    {msg.sender_fullname || msg.sender_username}
                  </div>
                )}

                <div
                  className="px-3 py-2"
                  style={{
                    backgroundColor: msg.is_deleted
                      ? "transparent"
                      : isMine
                      ? "var(--chat-purple)"
                      : "#2a2a3e",
                    borderRadius: isMine
                      ? "16px 16px 4px 16px"
                      : "16px 16px 16px 4px",
                    border: msg.is_deleted ? "1px solid #2a2a3e" : "none",
                    cursor: isMine && !msg.is_deleted ? "context-menu" : "default",
                  }}
                >
                  {msg.is_deleted ? (
                    <span style={{ color: "#828282", fontStyle: "italic", fontSize: "1.3rem" }}>
                      Message deleted
                    </span>
                  ) : (
                    <>
                      {msg.media_url && (
                        <img
                          src={msg.media_url}
                          alt="attachment"
                          className="rounded mb-1"
                          style={{ maxWidth: "100%", maxHeight: "300px" }}
                        />
                      )}
                      {msg.content && (
                        <p
                          className="mb-0 text-white"
                          style={{
                            fontSize: "1.4rem",
                            wordWrap: "break-word",
                            lineHeight: "1.4",
                          }}
                        >
                          {msg.content}
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* Timestamp */}
                <div
                  className={`mt-1 ${isMine ? "text-end" : "text-start"}`}
                  style={{ color: "#828282", fontSize: "1rem", paddingLeft: "4px", paddingRight: "4px" }}
                >
                  {formatMessageTime(msg.created_at)}
                </div>
              </div>

              {/* My avatar */}
              {isMine && (
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    minWidth: "32px",
                    marginLeft: "8px",
                    alignSelf: "flex-end",
                  }}
                >
                  <img
                    src={msg.sender_avatar && msg.sender_avatar !== "sampleAvatar.png" ? msg.sender_avatar : sampleAvatar}
                    alt=""
                    className="rounded-circle w-100 h-100"
                    style={{ objectFit: "cover" }}
                  />
                </div>
              )}
            </div>
          );
        })}

        {/* Typing indicator */}
        {typingUsersList.length > 0 && (
          <div className="d-flex justify-content-start mb-2">
            <div
              className="px-3 py-2 rounded-pill"
              style={{ backgroundColor: "#2a2a3e" }}
            >
              <span style={{ color: "#828282", fontSize: "1.3rem" }}>
                <span className="typing-dots">
                  <span style={{ animation: "typingDot 1.4s infinite", animationDelay: "0s" }}>.</span>
                  <span style={{ animation: "typingDot 1.4s infinite", animationDelay: "0.2s" }}>.</span>
                  <span style={{ animation: "typingDot 1.4s infinite", animationDelay: "0.4s" }}>.</span>
                </span>
              </span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div
        className="d-flex align-items-center p-3 gap-2"
        style={{
          borderTop: "1px solid #2a2a3e",
          backgroundColor: "#1e1e2e",
        }}
      >
        {/* Text input with emoji and camera */}
        <div
          className="d-flex align-items-center flex-grow-1 rounded-pill px-3"
          style={{
            backgroundColor: "#2a2a3e",
            height: "44px",
          }}
        >
          <ion-icon
            name="happy-outline"
            style={{ color: "#828282", fontSize: "2rem", cursor: "pointer", minWidth: "24px" }}
          ></ion-icon>
          <input
            type="text"
            placeholder="Message..."
            value={newMessageText}
            onChange={handleTypingInput}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            className="border-0 bg-transparent text-white mx-2 w-100"
            style={{ outline: "none", fontSize: "1.4rem" }}
          />
          <ion-icon
            name="camera-outline"
            style={{ color: "#828282", fontSize: "2rem", cursor: "pointer", minWidth: "24px" }}
          ></ion-icon>
        </div>

        {/* Send / Mic button */}
        <button
          className="btn rounded-circle d-flex align-items-center justify-content-center"
          onClick={handleSendMessage}
          disabled={!newMessageText.trim() || sending}
          style={{
            backgroundColor: "var(--chat-purple)",
            color: "white",
            width: "44px",
            height: "44px",
            minWidth: "44px",
            border: "none",
            opacity: newMessageText.trim() ? 1 : 0.5,
          }}
        >
          <ion-icon
            name={newMessageText.trim() ? "send" : "mic"}
            style={{ fontSize: "1.8rem" }}
          ></ion-icon>
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalMessage && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 9999 }}
          onClick={() => setDeleteModalMessage(null)}
        >
          <div
            className="rounded-4 p-4 text-center"
            style={{
              backgroundColor: "#1e1e2e",
              border: "1px solid #2a2a3e",
              maxWidth: "320px",
              width: "90%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3">
              <ion-icon
                name="trash-outline"
                style={{ fontSize: "4rem", color: "var(--error-red)" }}
              ></ion-icon>
            </div>
            <p className="text-white mb-1" style={{ fontSize: "1.6rem", fontWeight: "600" }}>
              Delete Message
            </p>
            <p style={{ color: "#828282", fontSize: "1.3rem" }} className="mb-4">
              Are you sure you want to delete this message?
            </p>
            <div className="d-flex gap-2">
              <button
                className="btn flex-grow-1 rounded-pill"
                onClick={() => setDeleteModalMessage(null)}
                style={{
                  backgroundColor: "#2a2a3e",
                  color: "white",
                  border: "none",
                  padding: "10px",
                  fontSize: "1.3rem",
                }}
              >
                Cancel
              </button>
              <button
                className="btn flex-grow-1 rounded-pill"
                onClick={() => handleDeleteMessage(deleteModalMessage.message_id)}
                style={{
                  backgroundColor: "var(--error-red)",
                  color: "white",
                  border: "none",
                  padding: "10px",
                  fontSize: "1.3rem",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Call Overlay */}
      {inCall && (
        <VideoCallOverlay
          socket={socket}
          currentUserId={currentUserId}
          currentUsername={currentUser?.username}
          targetUserId={conversation?.other_user_id}
          targetUsername={conversation?.other_username || displayName}
          targetAvatar={resolvedAvatar || sampleAvatar}
          callType={inCall}
          onEnd={() => setInCall(false)}
        />
      )}
    </div>
  );
}

function useCallerRingtone(isRinging) {
  const ctxRef = useRef(null);
  useEffect(() => {
    if (!isRinging) { cleanup(); return; }
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx;
      const gain = ctx.createGain();
      gain.gain.value = 0.15;
      gain.connect(ctx.destination);
      function beep() {
        if (!ctxRef.current) return;
        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = 440;
        const env = ctx.createGain();
        env.gain.setValueAtTime(0, now);
        env.gain.linearRampToValueAtTime(0.15, now + 0.05);
        env.gain.setValueAtTime(0.15, now + 0.8);
        env.gain.linearRampToValueAtTime(0, now + 0.9);
        osc.connect(env);
        env.connect(gain);
        osc.start(now);
        osc.stop(now + 0.9);
        ctxRef.current._timer = setTimeout(beep, 3000);
      }
      beep();
    } catch {}
    function cleanup() {
      if (ctxRef.current) {
        clearTimeout(ctxRef.current._timer);
        ctxRef.current.close().catch(() => {});
        ctxRef.current = null;
      }
    }
    return cleanup;
  }, [isRinging]);
}

function VideoCallOverlay({ socket: socketProp, currentUserId, currentUsername, targetUserId, targetUsername, targetAvatar, callType = "video", onEnd }) {
  const socketCtx = useContext(SocketContext);
  const socket = socketProp || socketCtx?.socket || socketCtx?.socketRef?.current;
  const isVideoCall = callType === "video";
  const [callStatus, setCallStatus] = useState("calling"); // calling, connected, failed
  const [statusMsg, setStatusMsg] = useState("Calling...");
  const [isMuted, setIsMuted] = useState(false);
  const [isCamOff, setIsCamOff] = useState(false);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerRef = useRef(null);
  const streamRef = useRef(null);
  const mountedRef = useRef(true);
  const iceServersRef = useRef(FALLBACK_ICE_SERVERS);

  // Play ringback tone while calling
  useCallerRingtone(callStatus === "calling");

  useEffect(() => {
    mountedRef.current = true;

    // Fetch TURN credentials
    fetchIceServers().then((servers) => { iceServersRef.current = servers; });

    async function startCall() {
      // Get media first
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({ video: isVideoCall, audio: true });
        if (!mountedRef.current) { mediaStream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = mediaStream;
        if (localVideoRef.current) localVideoRef.current.srcObject = mediaStream;
      } catch {
        if (mountedRef.current) {
          setCallStatus("failed");
          setStatusMsg("Camera/mic access denied");
        }
        return;
      }

      // Emit direct call
      console.log("Emitting directCall", { targetUserId, currentUserId, socketId: socket?.id, connected: socket?.connected });
      socket.emit("directCall", {
        targetUserId,
        callerUsername: currentUsername || "User",
        callerUserId: currentUserId,
        callType: callType,
      });
    }

    function onMatched({ partnerId }) {
      console.log("MATCHED! partnerId:", partnerId, "stream:", !!streamRef.current, "mounted:", mountedRef.current);
      if (!mountedRef.current || !streamRef.current) { console.log("MATCHED but aborting - no stream or unmounted"); return; }
      setCallStatus("connected");

      const peer = new Peer({ initiator: true, trickle: true, stream: streamRef.current, config: { iceServers: iceServersRef.current } });

      peer.on("signal", (data) => {
        console.log("Peer signal (caller), sending to:", partnerId);
        socket.emit("webrtc-signal", { target: partnerId, signal: data });
      });
      peer.on("stream", (remoteStream) => {
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = remoteStream;
      });
      peer.on("close", () => { if (mountedRef.current) { setCallStatus("failed"); setStatusMsg("Call ended"); } });
      peer.on("error", () => { if (mountedRef.current) { setCallStatus("failed"); setStatusMsg("Connection error"); } });

      peerRef.current = peer;
    }

    function onSignal({ from, signal }) {
      if (!mountedRef.current) return;
      if (peerRef.current) {
        peerRef.current.signal(signal);
      } else if (streamRef.current) {
        // Answering side (if this overlay is reused)
        setCallStatus("connected");
        const peer = new Peer({ initiator: false, trickle: true, stream: streamRef.current, config: { iceServers: iceServersRef.current } });
        peer.on("signal", (data) => socket.emit("webrtc-signal", { target: from, signal: data }));
        peer.on("stream", (remoteStream) => {
          if (remoteVideoRef.current) remoteVideoRef.current.srcObject = remoteStream;
        });
        peer.on("close", () => { if (mountedRef.current) { setCallStatus("failed"); setStatusMsg("Call ended"); } });
        peer.on("error", () => { if (mountedRef.current) { setCallStatus("failed"); setStatusMsg("Connection error"); } });
        peerRef.current = peer;
        peer.signal(signal);
      }
    }

    function onCallFailed({ reason }) {
      if (mountedRef.current) { setCallStatus("failed"); setStatusMsg(reason || "User is not available"); }
    }
    function onCallRejected() {
      if (mountedRef.current) { setCallStatus("failed"); setStatusMsg("Call declined"); }
    }
    function onPartnerDisconnected() {
      if (mountedRef.current) { setCallStatus("failed"); setStatusMsg("Call ended"); }
    }
    function onCallEnded() {
      if (mountedRef.current) { setCallStatus("failed"); setStatusMsg("Call ended"); }
    }

    console.log("VideoCallOverlay effect:", { socket: !!socket, socketId: socket?.id, connected: socket?.connected, currentUserId, targetUserId });
    if (!socket) { console.log("VideoCallOverlay: socket is null!"); setCallStatus("failed"); setStatusMsg("Not connected"); return; }

    socket.on("matched", onMatched);
    socket.on("webrtc-signal", onSignal);
    socket.on("callFailed", onCallFailed);
    socket.on("callRejected", onCallRejected);
    socket.on("partnerDisconnected", onPartnerDisconnected);
    socket.on("callEnded", onCallEnded);

    startCall();

    return () => {
      mountedRef.current = false;
      if (socket) {
        socket.off("matched", onMatched);
        socket.off("webrtc-signal", onSignal);
        socket.off("callFailed", onCallFailed);
        socket.off("callRejected", onCallRejected);
        socket.off("partnerDisconnected", onPartnerDisconnected);
        socket.off("callEnded", onCallEnded);
      }
      if (peerRef.current) { peerRef.current.destroy(); peerRef.current = null; }
      if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); }
    };
  }, [socket, currentUserId, targetUserId]);

  const handleEndCall = () => {
    if (socket) socket.emit("hangup", { callEnder: socket.id });
    if (peerRef.current) { peerRef.current.destroy(); peerRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    onEnd();
  };

  const toggleMute = () => {
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
      setIsMuted(p => !p);
    }
  };

  const toggleCam = () => {
    if (streamRef.current) {
      streamRef.current.getVideoTracks().forEach(t => { t.enabled = !t.enabled; });
      setIsCamOff(p => !p);
    }
  };

  return (
    <div style={{
      position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: "#101010", zIndex: 1000,
      display: "flex", flexDirection: "column",
      borderRadius: "16px", overflow: "hidden",
    }}>
      {/* Remote video / calling state */}
      <div style={{ flex: 1, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {isVideoCall && (
          <video ref={remoteVideoRef} autoPlay playsInline style={{
            width: "100%", height: "100%", objectFit: "cover", position: "absolute", top: 0, left: 0,
            display: callStatus === "connected" ? "block" : "none",
          }} />
        )}
        {!isVideoCall && <audio ref={remoteVideoRef} autoPlay />}

        {(callStatus !== "connected" || !isVideoCall) && (
          <div style={{ textAlign: "center" }}>
            <img src={targetAvatar} alt="" style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", marginBottom: 16, border: "2px solid #b997ff" }} />
            <h3 style={{ color: "#fff", margin: "0 0 8px" }}>{targetUsername}</h3>
            <p style={{ color: "#aaa", fontSize: "0.9rem" }}>
              {callStatus === "connected" && !isVideoCall ? "On call..." : statusMsg}
            </p>
            {callStatus === "failed" && (
              <button onClick={onEnd} style={{
                marginTop: 12, backgroundColor: "rgba(255,255,255,0.1)", color: "#fff",
                border: "1px solid rgba(255,255,255,0.2)", borderRadius: 20, padding: "8px 24px",
                cursor: "pointer", fontSize: "0.9rem",
              }}>Close</button>
            )}
          </div>
        )}

        {/* Local video PiP — only for video calls */}
        {isVideoCall && (
          <div style={{
            position: "absolute", bottom: 80, right: 16,
            width: 120, height: 90, borderRadius: 10, overflow: "hidden",
            border: "2px solid rgba(185,151,255,0.4)", boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
          }}>
            <video ref={localVideoRef} autoPlay playsInline muted style={{
              width: "100%", height: "100%", objectFit: "cover", opacity: isCamOff ? 0.3 : 1,
            }} />
          </div>
        )}
      </div>

      {/* Controls */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: 16, padding: "16px 0", backgroundColor: "rgba(0,0,0,0.6)",
      }}>
        <button onClick={toggleMute} style={{
          width: 48, height: 48, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.15)",
          backgroundColor: isMuted ? "rgba(185,151,255,0.3)" : "rgba(255,255,255,0.1)", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <ion-icon name={isMuted ? "mic-off" : "mic"} style={{ color: isMuted ? "#ff6666" : "#fff", fontSize: "1.4rem" }}></ion-icon>
        </button>

        {isVideoCall && (
          <button onClick={toggleCam} style={{
            width: 48, height: 48, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.15)",
            backgroundColor: isCamOff ? "rgba(185,151,255,0.3)" : "rgba(255,255,255,0.1)", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <ion-icon name={isCamOff ? "videocam-off" : "videocam"} style={{ color: isCamOff ? "#ff6666" : "#fff", fontSize: "1.4rem" }}></ion-icon>
          </button>
        )}

        <button onClick={handleEndCall} style={{
          width: 56, height: 56, borderRadius: "50%", border: "none",
          backgroundColor: "#ff4444", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <ion-icon name="call" style={{ color: "#fff", fontSize: "1.6rem", transform: "rotate(135deg)" }}></ion-icon>
        </button>
      </div>
    </div>
  );
}
